import Dropdown from "../../components/Form/Dropdown";
import ImageUpload from "../../components/Form/ImageUpload";
import CustomInput from "../../components/Form/Input";
import Button from "../../components/Form/CustomButton";
import useForm from "../../hooks/useForm";
import { COMMON_FIELD_TYPES } from "../../constants/fieldTypes";
import {
  complaintReasons,
  firebaseUplaodFolders,
  referencesFormats,
} from "../../constants/commonConstants";
import showToast from "../../utils/toastNotifications";
import { useState } from "react";
import { useThunk } from "../../hooks/useThunk";
import { createRaistComplaint } from "../../store";
import { useNavigate, useParams } from "react-router-dom";
import ErrorDisplay from "../../error/ErrorDisplay";
import { isValidObjectId } from "../../utils/validationUtils";
import useFirebaseFileUpload from "../../hooks/useFirebaseFileUpload";

const RaiseComplaint = ({}) => {
  const navigate = useNavigate();

  // Get the orderID and collectionId from the URL
  const { orderId } = useParams();

  const {
    formData,
    errors,
    touched,
    handleInputChange,
    handleBlur,
    validateForm,
    setTouched,
    setFormData,
    clearError,
  } = useForm(
    {
      complaintReason: "",
      comment: "",
    },
    {
      complaintReason: true,
      comment: false,
    },
    {
      complaintReason: {
        type: COMMON_FIELD_TYPES.DROPDOWN,
        displayKey: "reason",
      },
      comment: COMMON_FIELD_TYPES.TEXT_FIELD,
    }
  );

  // Thunks
  const [doRaiseComplaint, isRaiseComplaintLoading, raiseComplaintError] =
    useThunk(createRaistComplaint);

  const [
    doUploadRefrencesToFirebase,
    isUploadRefrencesToFirebaseLoading,
    uploadRefrencesToFirebaseError,
  ] = useFirebaseFileUpload();

  const [uploadFiles, setUploadFiles] = useState([]);

  const onFileUpload = (files) => {
    setUploadFiles([...files]);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setTouched({
      complaintReason: true,
      comments: true,
    });
    if (!validateForm()) {
      console.log("Validation Failed");
      return;
    }

    let documents = [];
    // Upload files to firebase
    if (uploadFiles.length > 0) {
      const result = await doUploadRefrencesToFirebase(
        uploadFiles,
        firebaseUplaodFolders.F_COMPLAINTS
      );
      if (result?.success) {
        console.log("Files uploaded successfully", result.uploadedUrls);
        // Set the uploaded files to the form data
        documents = result.uploadedUrls;
      } else {
        console.log("Error uploading files", result.error);
        showToast("error", "Error uploading files");
        return;
      }
    }

    // Raise the complaint
    const result = await doRaiseComplaint({
      orderId: orderId,
      complaintReason: formData?.complaintReason || "",
      comments: formData?.comment || "",
      documents: documents.length > 0 ? documents : null,
    });
    if (result?.success) {
      showToast("success", "Complaint Raised successfully");
      setTimeout(() => {
        navigate("/client/order-history");
      }, 2000);
    } else {
      console.log("Error placing order", result.error);
      showToast("error", result?.error?.message);
    }
  };

  return (
    <div className="w-full px-[20px] lg:px-[100px] lg:mb-[88px]">
      <h1 className="text-left lg:text-center pt-6 lg:pt-12 text-level-2 lg:text-level-1 text-primary font-nonito_sans font-bold font-style: normal tracking-[.042px]">
        Raise Complaint
      </h1>
      {
        // Show ErrorDisplay if orderId is invalid or there is an error
        !isValidObjectId(orderId) || raiseComplaintError ? (
          <ErrorDisplay
            errorMessage={`${
              raiseComplaintError?.message || "Invalid Order ID"
            }`}
            buttonText="Back to Order History"
            onButtonClick={() => navigate("/client/order-history")}
          />
        ) : (
          <div className="mt-7 lg:mt-[40px] mb-16 p-5 lg:p-9 border border-[#CBD5E0] rounded-[20px] bg-white  ">
            <div>
              <div className="flex gap-2 items-center mb-5 lg:mb-6 ">
                <span className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-level-6">
                  1
                </span>
                <span className="ml-2 font-inter text-[20px] font-medium leading-[30px] ">
                  Complaint Details
                </span>
              </div>
              <Dropdown
                name="complaintReason"
                label="Complaint Reason"
                placeholder="Select Complaint Reason"
                idKey="_id"
                displayKey="reason"
                options={complaintReasons}
                // defaultOption={formData.category}
                onSelect={(selectedOption) =>
                  handleInputChange("complaintReason", selectedOption)
                }
                onFocus={() => clearError("complaintReason")} // Clear error when user interacts again
                onBlur={handleBlur}
                error={touched.complaintReason && errors.complaintReason}
                isRequired={true}
                outerContainerStyle="mt-1 lg:mt-2 w-full max-w-[581px]"
                buttonClassName=" rounded-[4px] py-[13px]"
              />
              <CustomInput
                type="text"
                name="comment"
                id="comment"
                value={formData.comment}
                onChange={(e) => handleInputChange("comment", e.target.value)}
                onBlur={(e) => handleBlur("comment", e.target.value)}
                placeholder="Enter Any Comments"
                label="Comments"
                className="w-full gap-[30px] rounded-[4px] px-4 py-[13px] "
                outerContainerStyle="mt-6 lg:mt-[30px] mb-1 lg:mb-2"
              />
            </div>

            <div className="mt-8 lg:mt-12">
              <div className="flex items-center gap-2">
                <span className="w-6 h-6 rounded-full bg-primary flex items-center justify-center text-white text-level-6">
                  2
                </span>
                <span className="ml-2 font-inter text-[20px] font-medium leading-[30px]">
                  Additional Details
                </span>
              </div>

              <ImageUpload
                label="Images"
                accept={[referencesFormats.JPEG_JPG, referencesFormats.PNG]}
                outerContainerStyle="mt-4 mb-1 lg:mt-6 lg:mb-2"
                onFileUpload={onFileUpload}
                uploadFiles={uploadFiles}
                isDisabled={isUploadRefrencesToFirebaseLoading}
                isLoading={isUploadRefrencesToFirebaseLoading}
                uploadText = "Jpeg, Jpg, png, files are allowed"
                numberOfFiles={5}        
              />
            </div>
            <hr className="mt-12" />
            <div className="flex mt-12 justify-end">
              <Button
                buttonText="Submit"
                bgColor="bg-primary"
                textColor="text-white"
                borderColor="border-transparent"
                loading={
                  isRaiseComplaintLoading || isUploadRefrencesToFirebaseLoading
                }
                loaderColor="white"
                disabled={
                  isRaiseComplaintLoading || isUploadRefrencesToFirebaseLoading
                }
                onClick={handleSubmit}
                className="w-[120px] h-[46px] px-4 py-4 font-inter text-level-6 text-white rounded-xl gap-4"
              />
              <button
                type="button"
                onClick={() => navigate("/client/order-history")}
                className="ml-9 font-inter text-level-6 text-primary font-semibold leading-[22px] 
                  not-italic"
              >
                Cancel
              </button>
            </div>
          </div>
        )
      }
    </div>
  );
};
export default RaiseComplaint;
